import { FC, ReactNode, useEffect, useRef } from 'react'
import { AnotacaoType, IconPaths } from '../../../../../../_metronic/helpers'
import { Button } from '../../../../../components'
import { AtendimentoView } from '../../../atendimento/core/_models'
import { FormularioAtendimentoList } from '../../../avaliacoes/avaliacoes-pendentes-list/core/_models'
import { EtapaParecer as Etapa } from '../core/etapaParecer'

/** Cartão da coluna do parecer, com título focável para anunciar a troca de etapa. */
const Cartao: FC<{ titulo: string; children: ReactNode }> = ({ titulo, children }) => {
  const tituloRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => tituloRef.current?.focus(), [titulo])
  return (
    <section aria-labelledby='parecer-etapa-titulo' className='card card-bordered'>
      <div className='card-body p-6 d-flex flex-column gap-4'>
        <h2 id='parecer-etapa-titulo' ref={tituloRef} tabIndex={-1} className='fs-4 fw-bold mb-0'>
          {titulo}
        </h2>
        {children}
      </div>
    </section>
  )
}

type Props = {
  /** Qualquer etapa menos `responder`, que é o formulário. */
  etapa: Exclude<Etapa, { tipo: 'responder' }>
  atendimento: AtendimentoView
  onAssumir: () => void
  assumindo: boolean
  onAssinar: () => void
  assinando: boolean
  onEscolherFormulario: (formulario: FormularioAtendimentoList) => void
}

/** O que a coluna do parecer mostra quando o teleconsultor ainda não está respondendo. */
export const EtapaParecer: FC<Props> = ({
  etapa,
  atendimento,
  onAssumir,
  assumindo,
  onAssinar,
  assinando,
  onEscolherFormulario,
}) => {
  switch (etapa.tipo) {
    case 'cancelada':
      return (
        <Cartao titulo='Interconsulta cancelada'>
          <p className='mb-0'>Interconsulta cancelada não recebe parecer.</p>
        </Cartao>
      )

    // A conclusão leva de volta à lista; aqui só chega quem abre uma já finalizada.
    case 'finalizada':
      return (
        <Cartao titulo='Interconsulta finalizada'>
          <p className='mb-0'>O parecer desta interconsulta já foi finalizado.</p>
        </Cartao>
      )

    case 'aguardandoAssinatura':
      return (
        <Cartao titulo='Parecer aguardando sua assinatura'>
          <p className='mb-0'>
            <strong>Assinatura pendente.</strong> A interconsulta foi finalizada, mas o parecer
            ainda não foi assinado. Ela só é concluída, e o solicitante só é avisado, depois da
            assinatura. Não é preciso reabrir o atendimento.
          </p>
          <div className='d-flex flex-wrap gap-3'>
            <Button
              className='btn-primary'
              icon={IconPaths.Pen}
              onClick={onAssinar}
              disabled={assinando}
            >
              Assinar parecer
            </Button>
          </div>
        </Cartao>
      )

    case 'pendente': {
      const pendencias =
        atendimento.anotacoes?.filter((anotacao) => anotacao.tipo === AnotacaoType.Pendencia) ?? []
      return (
        <Cartao titulo='Pendente com o solicitante'>
          <p className='mb-0'>
            A interconsulta aguarda o solicitante resolver a pendência. O parecer fica disponível
            depois disso.
          </p>
          {pendencias.length > 0 && (
            <ul className='list-unstyled d-flex flex-column gap-3 mb-0'>
              {pendencias.map((anotacao, index) => (
                <li key={anotacao.id ?? index} className='border rounded px-4 py-3'>
                  <div className='text-gray-700 fs-7'>
                    {anotacao.dataFormatada} — {anotacao.usuario?.nome}
                  </div>
                  <div className='text-gray-900'>{anotacao.anotacao}</div>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      )
    }

    case 'somenteLeitura':
      return (
        <Cartao titulo='Somente leitura'>
          <p className='mb-0'>
            Só um profissional de saúde com vínculo ativo na instituição do paciente responde o
            parecer.
          </p>
        </Cartao>
      )

    case 'solicitacaoIncompleta':
      return (
        <Cartao titulo='Solicitação incompleta'>
          <p className='mb-0'>
            O solicitante ainda não preencheu a solicitação de parecer. Ela aparece aqui assim que
            for enviada.
          </p>
        </Cartao>
      )

    case 'semFormulario':
      return (
        <Cartao titulo='Sem parecer para responder'>
          <p className='mb-0'>Esta interconsulta não tem formulário de parecer em aberto.</p>
        </Cartao>
      )

    case 'assumir':
      return (
        <Cartao titulo='Você ainda não assumiu esta interconsulta'>
          <p className='mb-0'>
            {etapa.responsavelAtual
              ? `Está em atendimento por ${
                  etapa.responsavelAtual.nome ?? 'outro profissional'
                }. Ao responder, a interconsulta passa para você.`
              : 'Leia a solicitação ao lado. Ao responder, você passa a ser o responsável pela interconsulta.'}
          </p>
          <div className='d-flex flex-wrap gap-3'>
            <Button
              className='btn-primary'
              icon={IconPaths.NewFile}
              onClick={onAssumir}
              disabled={assumindo}
            >
              Responder parecer
            </Button>
          </div>
        </Cartao>
      )

    case 'selecionarFormulario':
      return (
        <Cartao titulo='Selecionar formulário'>
          <p className='mb-0'>Esta interconsulta tem mais de um formulário em aberto.</p>
          <div className='d-flex flex-column gap-2'>
            {etapa.formularios.map((opcao) => (
              <Button
                key={opcao.id}
                variant='secondary-outline'
                onClick={() => onEscolherFormulario(opcao)}
              >
                {opcao.formulario?.nome}
              </Button>
            ))}
          </div>
        </Cartao>
      )
  }
}
